import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Grid, Typography, Button, TextField, FormControl,
  InputLabel, Select, MenuItem, Chip, Alert, CircularProgress,
  Divider, IconButton, Switch, FormControlLabel,
} from '@mui/material';
import {
  Close, Save, Send, Person, School, Psychology, Assessment,
  Flag, EventNote, Build, EmojiEvents, ArrowForward, QueryStats,
  GpsFixed, GpsNotFixed, MyLocation,
} from '@mui/icons-material';
import axios from 'axios';
import { API_ENDPOINTS } from '../config';
import { getErrorMessage } from '../utils/error';
import { BRAND } from '../theme';

const h = (token) => ({ Authorization: `Bearer ${token}` });

/* ── Visit type definitions ─────────────────────────────────────────────── */
const VISIT_TYPES = [
  {
    value: 'data_update',
    label: 'Data Collection Visit',
    icon: <Assessment />,
    desc: 'Collect and record MSME metrics',
    color: BRAND.gopaGoldDark,
  },
  {
    value: 'one_on_one',
    label: 'One-on-One Visit',
    icon: <Person />,
    desc: 'Direct visit to one MSME',
    color: BRAND.primaryMain,
  },
  {
    value: 'training',
    label: 'Training Visit',
    icon: <School />,
    desc: 'Group or individual training session',
    color: BRAND.programmeGreen,
  },
  {
    value: 'coaching',
    label: 'Business Coaching',
    icon: <Psychology />,
    desc: 'Structured coaching session',
    color: BRAND.proSlate,
  },
  {
    value: 'annual_review',
    label: 'Annual Review',
    icon: <QueryStats />,
    desc: 'In-depth qualitative review of MSME data',
    color: BRAND.gizRed,
  },
  {
    value: 'bcp_facilitation',
    label: 'BCP Facilitation & Accountability',
    icon: <Flag />,
    desc: 'BCP lead facilitator accountability & field verification',
    color: BRAND.primaryMain,
  },
];

/* ── Per-type field labels ───────────────────────────────────────────────── */
const TYPE_CONFIG = {
  one_on_one: {
    context_label:    'Business Reality Observed',
    context_hint:     'What is the current operational reality of the business? What did you observe upon arrival?',
    delivered_label:  'General Support & Coaching Delivered',
    delivered_hint:   'What coaching frameworks, analysis, or assistance did you deliver to the business owner?',
    outcomes_label:   'Key Observations & Changes Noted',
    outcomes_hint:    'What operational progress, concerns, or findings did you note? What changed since the last visit?',
    msme_label:       'MSME Agreed Commitments',
    msme_hint:        'What specific tasks did the business owner commit to completing before the next visit?',
    bge_label:        'BGE Preparation for Next Visit',
    bge_hint:         'What will you research, prepare, or bring as BGE before returning?',
    tools_label:      'Key Business Tools Supported / Installed',
    tools_hint:       'Supporting MSME to install, configure, and adopt key business tools (e.g. ISM, One Tap POS, cashbooks)',
    show_participants: false,
    show_delivery:     false,
    show_focus:        false,
    show_reflections:  true,
  },
  training: {
    context_label:    'Topics Covered',
    context_hint:     'What training topics, modules or content were covered in this session?',
    delivered_label:  'How the Training Was Delivered',
    delivered_hint:   'Describe the session flow — methods used, activities, group exercises, demonstrations.',
    outcomes_label:   'Participant Engagement & Takeaways',
    outcomes_hint:    'How did participants engage? What were the key learning outcomes or takeaways from the session?',
    msme_label:       'Assignments / Tasks Given',
    msme_hint:        'What tasks, homework or commitments did participants take away from this training?',
    bge_label:        'Next Session Plan',
    bge_hint:         'What will the next training session cover? Any preparation required?',
    tools_label:      'Training Materials & Handouts',
    show_participants: true,
    show_delivery:     true,
    show_focus:        false,
  },
  coaching: {
    context_label:    'What Was Discussed',
    context_hint:     'What challenges, opportunities or business topics did you discuss with the owner?',
    delivered_label:  'Coaching Delivered',
    delivered_hint:   'What coaching, frameworks, analysis or structured guidance did you apply during this session?',
    outcomes_label:   "Owner's Insights & Progress",
    outcomes_hint:    'What did the business owner realise, learn or achieve during this session? What shifted?',
    msme_label:       'Owner Commitments',
    msme_hint:        'What specific actions did the business owner commit to completing before the next coaching session?',
    bge_label:        'BGE Follow-up',
    bge_hint:         'What coaching preparation, research or follow-up will you carry out before the next session?',
    tools_label:      'Tools & Frameworks Used',
    show_participants: false,
    show_delivery:     false,
    show_focus:        true,
    show_reflections:  true,
  },
  data_update: {
    context_label:    'Business Status Observed',
    context_hint:     'What is the current state of the business? What did you see, measure or verify during this data collection visit?',
    delivered_label:  'Data Collection Process',
    delivered_hint:   'How did you collect the data — what did you ask, observe, verify or record? Who did you engage with?',
    outcomes_label:   'Key Findings & Changes Noted',
    outcomes_hint:    'What are the notable findings? What has changed compared to previous records?',
    msme_label:       'Business Owner Actions Required',
    msme_hint:        'Any documents, records or information the owner needs to provide or prepare.',
    bge_label:        'Data Entry & Follow-up',
    bge_hint:         'What still needs to be entered into the system? Any follow-up data collection needed?',
    tools_label:      'Data Collection Tools & Forms Used',
    show_participants: false,
    show_delivery:     false,
    show_focus:        false,
    show_data_quality: false,
  },
  annual_review: {
    context_label:    'Business Status at Review',
    context_hint:     'Describe the overall state of the business at the time of this annual review. What did you observe — operations, premises, staff, activity levels?',
    delivered_label:  'Review Process & Questions Asked',
    delivered_hint:   'Describe how you conducted the review. What data did you seek to verify? How cooperative was the owner? Were there other people present?',
    outcomes_label:   'Key Findings & Year-on-Year Changes',
    outcomes_hint:    'What are the significant findings from this review? How does the business compare to last year\'s data? What has improved, declined, or stalled?',
    msme_label:       'Records & Documentation Needed',
    msme_hint:        'What records, documents or information does the owner still need to provide, locate or organise before data entry is complete?',
    bge_label:        'Data Entry & Verification Follow-up',
    bge_hint:         'What data still needs to be entered or cross-checked in the system? Is a follow-up visit required to complete verification?',
    tools_label:      'Data Collection Forms & Tools Used',
    show_participants: false,
    show_delivery:     false,
    show_focus:        false,
    show_data_quality: true,
    show_delivered:    false,
  },
  bcp_facilitation: {
    context_label:    'Owner & Team Engagement Reality',
    context_hint:     'How receptive was leadership? Did staff actively contribute to identifying operational failure points?',
    delivered_label:  'Field Engagement & Enterprise Participants',
    delivered_hint:   'List enterprise participants (Name & Role), session dates, and total hours on-site.',
    outcomes_label:   'The Acid Test — Concrete Operational Change',
    outcomes_hint:    'What is the ONE tangible operational change the enterprise demonstrated or committed to executing starting immediately?',
    msme_label:       'Primary Residual Vulnerability Observed',
    msme_hint:        'What remains the single greatest unmitigated threat to this business that PRUDEV II should monitor?',
    bge_label:        'Strategic Follow-up Coaching Recommendation',
    bge_hint:         'What is the primary focus for subsequent BGE coaching visits (e.g. generator financing, cashbook verification)?',
    focus_label:      'Owner & Team Engagement Areas (Select all that apply)',
    focus_options: [
      'Business Operations & Continuity',
      'Financial Resilience & Cash Runway',
      'Key Staff, Roles & Cross-Training',
      'Machinery, Equipment & Maintenance',
      'Power & Utility Infrastructure',
      'Supply Chain & Sourcing Resilience',
      'Customer Communication & Retention',
      'Compliance & Business Registration',
      'Record Keeping & Internal Controls',
      'Other',
    ],
    show_participants: false,
    show_delivery:     false,
    show_focus:        true,
    multi_focus:       true,
    show_tools:        false,
    show_reflections:  true,
  },
};

const DEFAULT_CONFIG = TYPE_CONFIG.one_on_one;

const DELIVERY_METHODS = [
  'Lecture / Presentation',
  'Workshop / Group Activity',
  'Practical Demonstration',
  'Peer Discussion',
  'One-on-one within group',
  'Mixed methods',
];

const COACHING_FOCUS_AREAS = [
  'Financial Management',
  'Marketing & Sales',
  'Business Operations',
  'Human Resources',
  'Business Strategy & Planning',
  'Digital Tools & Technology',
  'Record Keeping',
  'Customer Service',
  'Supply Chain & Procurement',
  'Compliance & Registration',
  'Other',
];

const TOOLS_OPTIONS = [
  'ISM (Standard / Pro) — Installed / Supported',
  'One Tap POS — Installed / Supported',
  'Structured Physical Cashbook',
  'Zoho Books / Digital Accounting',
  'Mobile money setup support',
  'Digital sales / inventory tool',
  'Digital tools demonstration',
  'Business registration guidance (URSB / TIN)',
  'UNBS / quality compliance checklist',
  'Financial tracking spreadsheet',
  'Budget / cash flow tool',
  'Calculator / costing tool',
  'Sales tracking sheet',
  'Business plan template',
  'Marketing materials / flyer template',
  'Training manual / handout',
  'HR policy template',
  'Diagnostic / assessment tool',
  'Referral / recommendation letter',
];

/* Tools are stored as comma-separated string in the model */
const parseTools = (str) =>
  str ? str.split(',').map(s => s.trim()).filter(Boolean) : [];

const serializeTools = (arr) => arr.join(', ');

/* ── Empty form ─────────────────────────────────────────────────────────── */
const EMPTY_FORM = {
  msme:                '',
  visit_type:          'data_update',
  visit_date:          new Date().toISOString().slice(0, 10),
  status:              'draft',
  // structured sections
  visit_objectives:    '',
  stated_purpose:      '',
  business_overview:   '',
  delivery_method:     '',
  participant_count:   '',
  coaching_focus_area: '',
  support_provided:    '',
  advice_delivered:    '',
  tools_provided:      '',   // comma-separated
  key_achievement:     '',
  concrete_takeaway:   '',
  challenges_identified: '',
  action_plan:         '',
  recommendations:     '',
  msme_visible_next_step: '',
  next_steps:          '',
  additional_notes:    '',
  // data quality (annual_review visits)
  data_confidence_level:       '',
  records_sighted:             null,
  owner_certainty_observation: '',
  data_collection_challenges:  '',
  // GPS location captured at time of visit
  visit_latitude:      null,
  visit_longitude:     null,
  visit_gps_accuracy:  null,
  // Instant SMS Action Handout to MSME
  send_sms:            true,
  sms_custom_message:  '',
};

/* ── Helpers ─────────────────────────────────────────────────────────────── */
function SectionBlock({ icon, title, color = BRAND.primaryMain, children }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Box sx={{
          width: 32, height: 32, borderRadius: 1, bgcolor: color,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {React.cloneElement(icon, { sx: { fontSize: 16, color: '#fff' } })}
        </Box>
        <Typography fontWeight={700} fontSize={14}>{title}</Typography>
      </Box>
      {children}
    </Box>
  );
}

/* ── Draft persistence helpers ───────────────────────────────────────────── */
function draftKey(editingReport, msmeId) {
  if (editingReport) return `visit_report_draft_edit_${editingReport.id}`;
  return `visit_report_draft_new_${msmeId || 'new'}`;
}

function readDraft(key) {
  try { return JSON.parse(localStorage.getItem(key)); } catch { return null; }
}

function writeDraft(key, form, tools, toolsOther) {
  try {
    localStorage.setItem(key, JSON.stringify({ form, tools, toolsOther, savedAt: Date.now() }));
  } catch { /* storage full or unavailable */ }
}

function clearDraft(key) {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

/* ── Coordinate round helper (6 decimal places max for GPS) ──────────────── */
const roundCoord = (val) => {
  if (val === null || val === undefined || val === '') return null;
  const num = Number(val);
  return isNaN(num) ? null : parseFloat(num.toFixed(6));
};

/* ── Main component ──────────────────────────────────────────────────────── */
export default function VisitReportForm({
  open, onClose, onSaved, msme: preselectedMsme, msmes = [],
  token, bgeProfile, editingReport = null, initialVisitType = 'data_update',
}) {
  const [form, setForm]   = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const [selectedTools, setSelectedTools] = useState([]);
  const [toolsOther, setToolsOther]       = useState('');
  const [draftBanner, setDraftBanner]     = useState(null); // { savedAt, key }
  const [gpsLoading, setGpsLoading]       = useState(false);
  const [gpsError, setGpsError]           = useState('');
  const draftTimerRef = useRef(null);
  const currentDraftKey = useRef('');

  /* Populate on open */
  useEffect(() => {
    if (!open) return;
    const initialMsmeId = preselectedMsme?.id || '';
    const key = draftKey(editingReport, initialMsmeId);
    currentDraftKey.current = key;

    if (editingReport) {
      const f = { ...EMPTY_FORM };
      Object.keys(EMPTY_FORM).forEach(k => {
        if (editingReport[k] != null) f[k] = String(editingReport[k]);
      });
      f.msme       = editingReport.msme       || '';
      f.visit_type = editingReport.visit_type || 'one_on_one';
      f.send_sms   = Boolean(editingReport.send_sms ?? true);
      f.sms_custom_message = editingReport.sms_custom_message || '';
      f.records_sighted    = editingReport.records_sighted   ?? null;
      f.visit_latitude     = roundCoord(editingReport.visit_latitude);
      f.visit_longitude    = roundCoord(editingReport.visit_longitude);
      f.visit_gps_accuracy = editingReport.visit_gps_accuracy != null ? Number(editingReport.visit_gps_accuracy) : null;
      f.participant_count  = editingReport.participant_count != null ? String(editingReport.participant_count) : '';
      const parsed = parseTools(editingReport.tools_provided || '');
      const knownSelected = parsed.filter(t => TOOLS_OPTIONS.includes(t));
      const otherText = parsed.filter(t => !TOOLS_OPTIONS.includes(t)).join(', ');
      setSelectedTools(knownSelected);
      setToolsOther(otherText);
      setForm(f);
      // For edits, check if there's an unsaved local draft that's newer
      const saved = readDraft(key);
      if (saved && saved.savedAt > (editingReport.updated_at ? new Date(editingReport.updated_at).getTime() : 0)) {
        setDraftBanner({ savedAt: saved.savedAt, key });
      } else {
        setDraftBanner(null);
      }
    } else {
      // For new reports, offer to restore any unsaved draft
      const saved = readDraft(key);
      if (saved) {
        setDraftBanner({ savedAt: saved.savedAt, key });
        // Pre-fill from saved draft immediately
        setForm(saved.form);
        setSelectedTools(saved.tools || []);
        setToolsOther(saved.toolsOther || '');
      } else {
        const initialMsme = preselectedMsme || (initialMsmeId ? msmes.find(x => x.id === Number(initialMsmeId)) : null);
        const msmeLat = roundCoord(initialMsme?.latitude);
        const msmeLng = roundCoord(initialMsme?.longitude);
        setForm({
          ...EMPTY_FORM,
          msme: initialMsmeId,
          visit_type: initialVisitType || 'data_update',
          visit_latitude: msmeLat,
          visit_longitude: msmeLng,
        });
        setSelectedTools([]);
        setToolsOther('');
        setDraftBanner(null);
      }
    }
    setError('');
  }, [open, editingReport, preselectedMsme, initialVisitType]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Auto-save draft to localStorage 800 ms after each change */
  const scheduleDraftSave = useCallback((f, tools, other) => {
    clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(() => {
      if (currentDraftKey.current) writeDraft(currentDraftKey.current, f, tools, other);
    }, 800);
  }, []);

  const set = (key, val) => {
    setForm(f => {
      const next = { ...f, [key]: val };
      scheduleDraftSave(next, selectedTools, toolsOther);
      return next;
    });
  };

  const handleMsmeChange = (newMsmeId) => {
    const selected = msmes.find(x => x.id === Number(newMsmeId) || x.id === newMsmeId);
    const newKey = draftKey(editingReport, newMsmeId);
    currentDraftKey.current = newKey;

    // Check if there is a saved draft for this newly selected MSME
    const saved = !editingReport ? readDraft(newKey) : null;
    if (saved) {
      setDraftBanner({ savedAt: saved.savedAt, key: newKey });
    } else {
      setDraftBanner(null);
    }

    setForm(f => {
      const next = { ...f, msme: newMsmeId };
      // If MSME has coordinates on file, use them if coordinates are empty or not live GPS
      if (selected?.latitude != null && selected?.longitude != null && (f.visit_latitude == null || f.visit_longitude == null || !f.visit_gps_accuracy)) {
        next.visit_latitude = roundCoord(selected.latitude);
        next.visit_longitude = roundCoord(selected.longitude);
        next.visit_gps_accuracy = null;
      }
      scheduleDraftSave(next, selectedTools, toolsOther);
      return next;
    });
  };

  const applyMsmeCoordinates = () => {
    const activeMsme = msmes.find(x => x.id === Number(form.msme) || x.id === form.msme) || preselectedMsme;
    if (activeMsme?.latitude != null && activeMsme?.longitude != null) {
      setForm(f => {
        const next = {
          ...f,
          visit_latitude: roundCoord(activeMsme.latitude),
          visit_longitude: roundCoord(activeMsme.longitude),
          visit_gps_accuracy: null,
        };
        scheduleDraftSave(next, selectedTools, toolsOther);
        return next;
      });
      setGpsError('');
    }
  };

  const toggleTool = (tool) =>
    setSelectedTools(prev => {
      const next = prev.includes(tool) ? prev.filter(t => t !== tool) : [...prev, tool];
      scheduleDraftSave(form, next, toolsOther);
      return next;
    });

  const restoreDraft = () => {
    const saved = readDraft(currentDraftKey.current);
    if (!saved) return;
    setForm(saved.form);
    setSelectedTools(saved.tools || []);
    setToolsOther(saved.toolsOther || '');
    setDraftBanner(null);
  };

  const discardDraft = () => {
    clearDraft(currentDraftKey.current);
    setDraftBanner(null);
    if (!editingReport) {
      const activeMsme = preselectedMsme || msmes.find(x => x.id === Number(form.msme) || x.id === form.msme);
      setForm({
        ...EMPTY_FORM,
        msme: form.msme || preselectedMsme?.id || '',
        visit_type: initialVisitType || 'data_update',
        visit_latitude: roundCoord(activeMsme?.latitude),
        visit_longitude: roundCoord(activeMsme?.longitude),
      });
      setSelectedTools([]);
      setToolsOther('');
    }
  };

  /* ── GPS capture ── */
  const captureGPS = useCallback(() => {
    if (!navigator.geolocation) {
      setGpsError('Geolocation is not supported by this browser.');
      return;
    }
    setGpsLoading(true);
    setGpsError('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm(f => {
          const next = {
            ...f,
            visit_latitude:     roundCoord(pos.coords.latitude),
            visit_longitude:    roundCoord(pos.coords.longitude),
            visit_gps_accuracy: pos.coords.accuracy ? Math.round(pos.coords.accuracy * 10) / 10 : null,
          };
          scheduleDraftSave(next, selectedTools, toolsOther);
          return next;
        });
        setGpsLoading(false);
      },
      (err) => {
        setGpsError(
          err.code === 1 ? 'Location permission denied.'
          : err.code === 2 ? 'Location unavailable — check GPS signal.'
          : 'Location request timed out.',
        );
        setGpsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  }, [scheduleDraftSave, selectedTools, toolsOther]);

  /* Auto-capture GPS when form opens (new reports only, and ONLY if neither MSME nor draft has coordinates and MSME is chosen) */
  useEffect(() => {
    if (open && !editingReport) {
      const currentMsme = preselectedMsme || msmes.find(x => x.id === Number(form.msme) || x.id === form.msme);
      const hasGps = (form.visit_latitude != null && form.visit_longitude != null) ||
                     (currentMsme?.latitude != null && currentMsme?.longitude != null);
      if (!hasGps && form.msme) {
        captureGPS();
      }
    }
  }, [open, form.msme]); // eslint-disable-line react-hooks/exhaustive-deps

  const cfg = TYPE_CONFIG[form.visit_type] || DEFAULT_CONFIG;
  const typeInfo = VISIT_TYPES.find(t => t.value === form.visit_type) || VISIT_TYPES[0];
  const selectedMsme = msmes.find(x => x.id === Number(form.msme) || x.id === form.msme)
    || preselectedMsme || null;

  const save = async (submitNow = false) => {
    if (!form.msme || !form.visit_date) {
      setError('MSME and visit date are required.'); return;
    }
    if (submitNow) {
      if (!form.stated_purpose || !form.stated_purpose.trim()) {
        setError('Please enter the stated purpose agreed with the owner upon arrival before submitting.');
        return;
      }
      if (cfg.show_reflections) {
        if (!form.advice_delivered || !form.advice_delivered.trim()) {
          setError('Please describe the immediate practical advice or demonstration delivered on the spot.');
          return;
        }
        if (!form.concrete_takeaway || !form.concrete_takeaway.trim()) {
          setError('Please record the one concrete change the MSME will do differently starting today (The Acid Test).');
          return;
        }
        if (!form.msme_visible_next_step || !form.msme_visible_next_step.trim()) {
          setError('Please record the visible next step agreed with the owner before departure.');
          return;
        }
      }
    }
    setSaving(true); setError('');

    const allTools = [
      ...selectedTools,
      ...toolsOther.split(',').map(s => s.trim()).filter(Boolean),
    ];

    const payload = {
      msme:                Number(form.msme),
      bge:                 bgeProfile?.id || null,
      visit_type:          form.visit_type,
      visit_date:          form.visit_date,
      status:              submitNow ? 'submitted' : 'draft',
      visit_objectives:    form.visit_objectives,
      stated_purpose:      form.stated_purpose,
      business_overview:   form.business_overview,
      delivery_method:     form.delivery_method,
      participant_count:   form.participant_count !== '' ? Number(form.participant_count) : null,
      coaching_focus_area: form.coaching_focus_area,
      support_provided:    form.support_provided,
      advice_delivered:    form.advice_delivered,
      tools_provided:      serializeTools(allTools),
      key_achievement:     form.key_achievement,
      concrete_takeaway:   form.concrete_takeaway,
      challenges_identified: form.challenges_identified,
      action_plan:         form.action_plan,
      recommendations:     form.recommendations,
      msme_visible_next_step: form.msme_visible_next_step,
      next_steps:          form.next_steps,
      additional_notes:    form.additional_notes,
      // SMS Action Handout to MSME
      send_sms:            submitNow && Boolean(form.send_sms),
      sms_custom_message:  form.sms_custom_message || undefined,
      // data quality
      data_confidence_level:       form.data_confidence_level,
      records_sighted:             form.records_sighted,
      owner_certainty_observation: form.owner_certainty_observation,
      data_collection_challenges:  form.data_collection_challenges,
      // GPS (properly rounded to 6 decimal places to prevent DRF 400 max_digits errors)
      visit_latitude:     roundCoord(form.visit_latitude),
      visit_longitude:    roundCoord(form.visit_longitude),
      visit_gps_accuracy: form.visit_gps_accuracy !== null && form.visit_gps_accuracy !== '' ? Number(form.visit_gps_accuracy) : null,
    };

    try {
      if (editingReport) {
        await axios.patch(`${API_ENDPOINTS.REPORTS}${editingReport.id}/`, payload, { headers: h(token) });
      } else {
        await axios.post(API_ENDPOINTS.REPORTS, payload, { headers: h(token) });
      }
      // Clear local draft on successful server save
      clearDraft(currentDraftKey.current);
      onSaved?.();
      onClose();
    } catch (e) {
      setError(getErrorMessage(e, 'Save failed.'));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{
        sx: {
          width:  { xs: 'calc(100vw - 16px)', md: '100%' },
          height: { xs: '96dvh', md: '92vh' },
          m: { xs: 1, md: 4 },
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        },
      }}>

      {/* GOPA Pro & GIZ dual brand accent stripe */}
      <Box sx={{
        height: 3, flexShrink: 0,
        background: `linear-gradient(90deg, ${BRAND.gopaGold} 0%, ${BRAND.gopaGold} 52%, ${BRAND.gizRed} 52%, ${BRAND.gizRed} 100%)`,
      }} />

      {/* Header */}
      <DialogTitle sx={{ pb: 1, bgcolor: typeInfo.color, color: '#fff' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography fontWeight={700} fontSize={17}>
              {editingReport ? 'Edit Visit Report' : 'New Visit Report'}
            </Typography>
            {selectedMsme && (
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                {selectedMsme.business_name}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#fff' }}><Close /></IconButton>
        </Box>
      </DialogTitle>

      {/* ── Draft recovery banner ── */}
      {draftBanner && (
        <Alert
          severity="warning"
          sx={{ mx: 2, mt: 1.5, mb: 0, flexShrink: 0 }}
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" color="inherit" variant="outlined"
                onClick={restoreDraft}>
                Restore
              </Button>
              <Button size="small" color="inherit"
                onClick={discardDraft}>
                Discard
              </Button>
            </Box>
          }
        >
          Unsaved draft from{' '}
          {new Date(draftBanner.savedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          {' '}— restore it?
        </Alert>
      )}

      <DialogContent sx={{ p: 0, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
        <Box sx={{
          height: '100%', minHeight: 0, display: 'flex',
          flexDirection: { xs: 'column', md: 'row' }, flexWrap: 'nowrap', width: '100%',
        }}>

          {/* ── Left sidebar ── */}
          <Box sx={{
            width: { xs: '100%', md: 280 },
            flexShrink: 0,
            maxHeight: { xs: '42dvh', md: 'none' },
            borderRight: { xs: 0, md: '1px solid #E8EDF2' },
            borderBottom: { xs: '1px solid #E8EDF2', md: 0 },
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            p: { xs: 1.5, sm: 2 },
            bgcolor: '#F8F9FA',
          }}>

            {/* MSME selector — always shown so report can be tied to the right MSME */}
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>MSME *</InputLabel>
              <Select value={form.msme} label="MSME *"
                onChange={e => handleMsmeChange(e.target.value)}>
                {msmes.map(x => (
                  <MenuItem key={x.id} value={x.id}>{x.business_name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Date */}
            <TextField fullWidth size="small" label="Visit Date *" type="date"
              InputLabelProps={{ shrink: true }} value={form.visit_date}
              onChange={e => set('visit_date', e.target.value)} sx={{ mb: 2 }} />

            {/* Visit type selector — 3 cards */}
            <Typography variant="overline" color="text.secondary" fontWeight={700} fontSize={10}
              display="block" sx={{ mb: 1 }}>
              Visit Type
            </Typography>
            {VISIT_TYPES.map(t => (
              <Box key={t.value}
                onClick={() => set('visit_type', t.value)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  p: 1.25, borderRadius: 1.5, cursor: 'pointer', mb: 0.75,
                  border: '2px solid',
                  borderColor: form.visit_type === t.value ? t.color : '#E8EDF2',
                  bgcolor:     form.visit_type === t.value ? `${t.color}12` : '#fff',
                  transition: 'all .12s',
                }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: 1, flexShrink: 0,
                  bgcolor: form.visit_type === t.value ? t.color : '#E8EDF2',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {React.cloneElement(t.icon, {
                    sx: { fontSize: 16, color: form.visit_type === t.value ? '#fff' : '#90A4AE' },
                  })}
                </Box>
                <Box>
                  <Typography fontSize={12} fontWeight={form.visit_type === t.value ? 700 : 500}
                    color={form.visit_type === t.value ? t.color : 'text.primary'}>
                    {t.label}
                  </Typography>
                  <Typography fontSize={10} color="text.secondary">{t.desc}</Typography>
                </Box>
              </Box>
            ))}

            {/* ── GPS location panel ── */}
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #E8EDF2' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="overline" color="text.secondary" fontWeight={700} fontSize={10}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <MyLocation sx={{ fontSize: 12 }} /> Visit Location
                </Typography>
                <IconButton size="small" onClick={captureGPS} disabled={gpsLoading}
                  title={form.visit_latitude ? 'Recapture GPS' : 'Capture GPS location'}
                  sx={{ p: 0.25 }}>
                  {gpsLoading
                    ? <CircularProgress size={14} />
                    : form.visit_latitude
                      ? <GpsFixed sx={{ fontSize: 16, color: '#2E7D32' }} />
                      : <GpsNotFixed sx={{ fontSize: 16, color: '#90A4AE' }} />
                  }
                </IconButton>
              </Box>

              {form.visit_latitude ? (
                <Box sx={{
                  bgcolor: '#E8F5E9', borderRadius: 1.5, p: 1.2, fontSize: 11, border: '1px solid #C8E6C9',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography fontSize={11} fontWeight={700} color="#2E7D32"
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <GpsFixed sx={{ fontSize: 13 }} /> Location Set
                    </Typography>
                    <Chip
                      size="small"
                      label={form.visit_gps_accuracy ? `Device GPS` : `MSME Profile`}
                      color={form.visit_gps_accuracy ? 'success' : 'default'}
                      sx={{ height: 18, fontSize: 9.5, fontWeight: 700 }}
                    />
                  </Box>
                  <Typography fontSize={10} color="text.secondary" sx={{ mt: 0.25, fontFamily: 'monospace' }}>
                    {Number(form.visit_latitude).toFixed(5)}, {Number(form.visit_longitude).toFixed(5)}
                  </Typography>
                  {form.visit_gps_accuracy && (
                    <Typography fontSize={10} color="text.secondary">
                      ±{Math.round(form.visit_gps_accuracy)} m device accuracy
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.75 }}>
                    <a
                      href={`https://maps.google.com/?q=${form.visit_latitude},${form.visit_longitude}`}
                      target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: 10.5, color: BRAND.primaryMain, fontWeight: 600, textDecoration: 'none' }}
                    >
                      View map ↗
                    </a>
                    {selectedMsme?.latitude != null && (
                      <Button
                        size="small"
                        onClick={applyMsmeCoordinates}
                        sx={{ fontSize: 9.5, textTransform: 'none', p: '2px 4px', minWidth: 0 }}
                      >
                        Reset to MSME GPS
                      </Button>
                    )}
                  </Box>
                </Box>
              ) : (
                <Box sx={{
                  bgcolor: '#F8F9FA', borderRadius: 1, p: 1, textAlign: 'center',
                }}>
                  {gpsLoading ? (
                    <Typography fontSize={11} color="text.secondary">Capturing location…</Typography>
                  ) : (
                    <>
                      <Typography fontSize={11} color="text.secondary">No location captured</Typography>
                      {gpsError && (
                        <Typography fontSize={10} color="error.main" sx={{ mt: 0.25 }}>{gpsError}</Typography>
                      )}
                      <Button size="small" sx={{ mt: 0.5, fontSize: 10 }}
                        startIcon={<GpsNotFixed sx={{ fontSize: 12 }} />}
                        onClick={captureGPS}>
                        Try again
                      </Button>
                    </>
                  )}
                </Box>
              )}
            </Box>
          </Box>

          {/* ── Main content panel ── */}
          <Box sx={{
            flex: 1, minHeight: 0, minWidth: 0,
            overflowY: 'auto', overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            p: { xs: 2, sm: 3 },
          }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* ── 1. OBJECTIVES & STATED PURPOSE ── */}
            <SectionBlock icon={<Flag />} title="1. Objectives & Stated Purpose (Opening Alignment)" color={typeInfo.color}>
              {cfg.show_reflections && (
                <Alert severity="info" sx={{ mb: 2, fontSize: 12, border: '1px solid #E7E5E4', bgcolor: '#FAF9F6' }}>
                  <strong>Reflection 1 — Opening Purpose:</strong> Open every visit by agreeing on a clear, single focus with the entrepreneur. Avoid project jargon like <em>"I am here for Visit #3"</em>.
                </Alert>
              )}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField fullWidth multiline rows={2} size="small"
                    label="Stated Purpose Agreed with Owner Upon Arrival *"
                    placeholder="Why we are here today: What mutual focus did you agree with the owner before starting? (e.g. 'Align on why cash does not match sales at closing and set up a daily cashbook together')"
                    value={form.stated_purpose}
                    onChange={e => set('stated_purpose', e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline rows={2} size="small"
                    label="Broader Visit Objectives"
                    placeholder={`e.g. ${form.visit_type === 'training'
                      ? 'Introduce financial record-keeping to participants and demonstrate the tracking spreadsheet.'
                      : form.visit_type === 'coaching'
                      ? 'Help the owner build a 3-month sales action plan and address pricing challenges.'
                      : 'Assess current business status, verify operations, and address previous action items.'}`}
                    value={form.visit_objectives}
                    onChange={e => set('visit_objectives', e.target.value)} />
                </Grid>
              </Grid>
            </SectionBlock>

            <Divider sx={{ my: 3 }} />

            {/* ── 2. CONTEXT (label changes per type) ── */}
            <SectionBlock icon={<EventNote />} title={cfg.context_label} color={typeInfo.color}>
              <Grid container spacing={2}>
                {/* Training-specific: delivery method + participants */}
                {cfg.show_delivery && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Delivery Method</InputLabel>
                      <Select value={form.delivery_method} label="Delivery Method"
                        onChange={e => set('delivery_method', e.target.value)}>
                        {DELIVERY_METHODS.map(m => (
                          <MenuItem key={m} value={m}>{m}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                {cfg.show_participants && (
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Number of Participants"
                      type="number" inputProps={{ min: 0 }}
                      value={form.participant_count}
                      onChange={e => set('participant_count', e.target.value)} />
                  </Grid>
                )}
                {/* Focus / Engagement Area selection */}
                {cfg.show_focus && (
                  <Grid item xs={12}>
                    {cfg.multi_focus ? (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}
                          display="block" sx={{ mb: 1 }}>
                          {cfg.focus_label || 'Engagement Areas (Select all that apply)'}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                          {(cfg.focus_options || COACHING_FOCUS_AREAS).map(area => {
                            const currentAreas = (form.coaching_focus_area || '')
                              .split(',')
                              .map(s => s.trim())
                              .filter(Boolean);
                            const checked = currentAreas.includes(area);
                            return (
                              <Chip
                                key={area}
                                label={area}
                                size="small"
                                clickable
                                variant={checked ? 'filled' : 'outlined'}
                                color={checked ? 'primary' : 'default'}
                                onClick={() => {
                                  const next = checked
                                    ? currentAreas.filter(a => a !== area)
                                    : [...currentAreas, area];
                                  set('coaching_focus_area', next.join(', '));
                                }}
                                sx={{ fontSize: 11 }}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    ) : (
                      <FormControl fullWidth size="small" sx={{ maxWidth: 360 }}>
                        <InputLabel>Coaching Focus Area</InputLabel>
                        <Select value={form.coaching_focus_area} label="Coaching Focus Area"
                          onChange={e => set('coaching_focus_area', e.target.value)}>
                          {COACHING_FOCUS_AREAS.map(a => (
                            <MenuItem key={a} value={a}>{a}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField fullWidth multiline rows={4} size="small"
                    label={cfg.context_label}
                    placeholder={cfg.context_hint}
                    value={form.business_overview}
                    onChange={e => set('business_overview', e.target.value)} />
                </Grid>
              </Grid>
            </SectionBlock>

            <Divider sx={{ my: 3 }} />

            {/* ── 3. DATA QUALITY (annual_review only) ── */}
            {cfg.show_data_quality && (
              <>
                <SectionBlock icon={<QueryStats />} title="Data Quality Assessment" color={typeInfo.color}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Data Confidence Level</InputLabel>
                        <Select
                          value={form.data_confidence_level}
                          label="Data Confidence Level"
                          onChange={e => set('data_confidence_level', e.target.value)}
                        >
                          <MenuItem value="confirmed">Confirmed — figures from actual records</MenuItem>
                          <MenuItem value="mostly_confident">Mostly confident — minor estimates only</MenuItem>
                          <MenuItem value="mixed">Mixed — owner unsure on several items</MenuItem>
                          <MenuItem value="largely_estimated">Largely estimated — few actual records</MenuItem>
                          <MenuItem value="unreliable">Unreliable — mostly guessing</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Physical Records Sighted?</InputLabel>
                        <Select
                          value={form.records_sighted === null ? '' : form.records_sighted ? 'yes' : 'no'}
                          label="Physical Records Sighted?"
                          onChange={e => set('records_sighted', e.target.value === 'yes' ? true : e.target.value === 'no' ? false : null)}
                        >
                          <MenuItem value="yes">Yes — BGE saw actual books / records</MenuItem>
                          <MenuItem value="no">No — relying on owner's verbal responses</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth multiline rows={4} size="small"
                        label="Owner Certainty & Confidence Observations"
                        placeholder="Was the owner confident in their answers or did they appear to be guessing? Which figures were they uncertain about — e.g. 'owner estimated sales from memory, unsure of exact figures for Q3', 'owner very clear on employee count but hesitant on profit figures'. Note anything that suggests the data may not reflect reality."
                        value={form.owner_certainty_observation}
                        onChange={e => set('owner_certainty_observation', e.target.value)} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth multiline rows={3} size="small"
                        label="Data Collection Challenges"
                        placeholder="Any difficulties encountered — reluctance to share figures, conflicting numbers given at different points, missing records, distractions, communication barriers, etc."
                        value={form.data_collection_challenges}
                        onChange={e => set('data_collection_challenges', e.target.value)} />
                    </Grid>
                  </Grid>
                </SectionBlock>
                <Divider sx={{ my: 3 }} />
              </>
            )}

            {/* ── 2. DELIVERED & TOOLS (hidden for annual_review) ── */}
            {cfg.show_delivered !== false && (
              <SectionBlock icon={<Build />} title="2. Diagnose AND Advise — Immediate Support Delivered" color={typeInfo.color}>
                {cfg.show_reflections && (
                  <Alert severity="info" sx={{ mb: 2, fontSize: 12, border: '1px solid #E7E5E4', bgcolor: '#FAF9F6' }}>
                    <strong>Reflection 2 — Diagnose AND Advise:</strong> Never leave a diagnosis hanging. If you identify a gap (e.g. mixed personal/business cash, unrecorded credit, pricing issues), immediately demonstrate or calculate the solution on the spot.
                  </Alert>
                )}
                <Grid container spacing={2}>
                  {cfg.show_reflections && (
                    <Grid item xs={12}>
                      <TextField fullWidth multiline rows={3} size="small"
                        label="Immediate Practical Advice & Demonstration Delivered on the Spot *"
                        placeholder="What concrete advice, calculation, or coaching did you demonstrate? (e.g. 'Demonstrated the 2-envelope cash separation rule; showed how to record customer credit before goods leave counter')"
                        value={form.advice_delivered}
                        onChange={e => set('advice_delivered', e.target.value)} />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <TextField fullWidth multiline rows={3} size="small"
                      label={cfg.delivered_label}
                      placeholder={cfg.delivered_hint}
                      value={form.support_provided}
                      onChange={e => set('support_provided', e.target.value)} />
                  </Grid>

                  {/* Tools multi-select */}
                  {cfg.show_tools !== false && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}
                        display="block" sx={{ mb: 0.5 }}>
                        {cfg.tools_label} — select all that apply
                      </Typography>
                      {cfg.tools_hint && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, fontSize: 11, fontStyle: 'italic' }}>
                          {cfg.tools_hint}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                        {TOOLS_OPTIONS.map(tool => {
                          const checked = selectedTools.includes(tool);
                          return (
                            <Chip key={tool} label={tool} size="small" clickable
                              variant={checked ? 'filled' : 'outlined'}
                              color={checked ? 'primary' : 'default'}
                              onClick={() => toggleTool(tool)}
                              sx={{ fontSize: 11 }} />
                          );
                        })}
                      </Box>
                      <TextField fullWidth size="small"
                        label="Other tools or materials (free text)"
                        placeholder="e.g. Custom pricing calculator, loan application template…"
                        value={toolsOther}
                        onChange={e => {
                          setToolsOther(e.target.value);
                          scheduleDraftSave(form, selectedTools, e.target.value);
                        }} />
                    </Grid>
                  )}
                </Grid>
              </SectionBlock>
            )}

            {cfg.show_delivered !== false && <Divider sx={{ my: 3 }} />}

            {/* ── 3. OUTCOMES & THE ACID TEST ── */}
            <SectionBlock icon={<EmojiEvents />} title="3. Outcomes & The Acid Test" color={typeInfo.color}>
              {cfg.show_reflections && (
                <Alert severity="warning" sx={{
                  mb: 2, fontSize: 12,
                  border: '1px solid #FDE68A',
                  bgcolor: '#FEF3C7',
                  color: '#92400E',
                  '& .MuiAlert-icon': { color: '#D97706' },
                }}>
                  <strong>Reflection 3 — The Acid Test:</strong> When you leave the business, can the owner name ONE concrete thing they now know to do differently? If not, what did the visit actually deliver?
                </Alert>
              )}
              <Grid container spacing={2}>
                {cfg.show_reflections && (
                  <Grid item xs={12}>
                    <TextField fullWidth multiline rows={2} size="small"
                      label="The One Concrete Change the MSME Will Do Differently Starting Today *"
                      placeholder="In the owner's own words: e.g. 'I will record every credit sale in the black book before the customer walks away with goods'"
                      value={form.concrete_takeaway}
                      onChange={e => set('concrete_takeaway', e.target.value)} />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField fullWidth multiline rows={2} size="small"
                    label={cfg.outcomes_label}
                    placeholder={cfg.outcomes_hint}
                    value={form.key_achievement}
                    onChange={e => set('key_achievement', e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline rows={2} size="small"
                    label="Challenges Encountered"
                    placeholder="Any difficulties faced — by the MSME, during the session, or in delivery."
                    value={form.challenges_identified}
                    onChange={e => set('challenges_identified', e.target.value)} />
                </Grid>
              </Grid>
            </SectionBlock>

            <Divider sx={{ my: 3 }} />

            {/* ── 4. CLOSING & VISIBLE NEXT STEPS ── */}
            <SectionBlock icon={<ArrowForward />} title="4. Closing & Visible Mutual Next Steps" color={typeInfo.color}>
              {cfg.show_reflections && (
                <Alert severity="info" sx={{ mb: 2, fontSize: 12, border: '1px solid #E7E5E4', bgcolor: '#FAF9F6' }}>
                  <strong>Reflection 4 — Visible Next Step:</strong> Every visit must close with a stated next step visible and confirmed with the MSME, not just stored invisibly in our system.
                </Alert>
              )}
              <Grid container spacing={2}>
                {cfg.show_reflections && (
                  <Grid item xs={12}>
                    <TextField fullWidth multiline rows={2} size="small"
                      label="Visible Next Step Agreed with the Owner Before Departure *"
                      placeholder="e.g. 'Owner will balance cashbook every evening for 7 days. BGE will return Sept 15 to review and introduce stock register.'"
                      value={form.msme_visible_next_step}
                      onChange={e => set('msme_visible_next_step', e.target.value)} />
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth multiline rows={2} size="small"
                    label={cfg.msme_label}
                    placeholder={cfg.msme_hint}
                    value={form.action_plan}
                    onChange={e => set('action_plan', e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth multiline rows={2} size="small"
                    label={cfg.bge_label}
                    placeholder={cfg.bge_hint}
                    value={form.recommendations}
                    onChange={e => set('recommendations', e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline rows={2} size="small"
                    label="Additional Notes"
                    value={form.additional_notes}
                    onChange={e => set('additional_notes', e.target.value)} />
                </Grid>
              </Grid>
            </SectionBlock>

            {/* ── 5. INSTANT SMS ACTION HANDOUT TO MSME ── */}
            {selectedMsme && (
              <Box sx={{
                mt: 3, p: 2, bgcolor: '#FEF9EE', border: '1px solid #FDE68A',
                borderRadius: 2, display: 'flex', flexDirection: 'column', gap: 1.5,
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700} color="#92400E">
                      📱 Send Instant SMS Action Summary to Entrepreneur
                    </Typography>
                    <Chip
                      size="small"
                      label={selectedMsme.phone || 'No phone recorded'}
                      color={selectedMsme.phone ? 'warning' : 'default'}
                      variant="outlined"
                      sx={{ height: 20, fontSize: 11 }}
                    />
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(form.send_sms)}
                        disabled={!selectedMsme.phone}
                        onChange={e => set('send_sms', e.target.checked)}
                        color="primary"
                      />
                    }
                    label={<Typography variant="caption" fontWeight={600}>{form.send_sms && selectedMsme.phone ? 'SMS Active' : 'Off'}</Typography>}
                  />
                </Box>
                {form.send_sms && selectedMsme.phone && (
                  <Box sx={{ p: 1.5, bgcolor: '#FFFFFF', borderRadius: 1.5, border: '1px solid #FDE68A' }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, fontWeight: 600 }}>
                      SMS Takeaway Preview (Dispatched to owner upon report submission):
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, color: '#1E293B', bgcolor: '#F8FAFC', p: 1, borderRadius: 1 }}>
                      {`Hello ${(selectedMsme.contact_person || selectedMsme.business_name || '').split(' ')[0] || 'Partner'}, thank you for your PRUDEV II coaching visit today with ${bgeProfile?.name?.split(' ')[0] || 'your BGE'}. Agreed Action: ${form.concrete_takeaway || form.action_plan || '[Agreed Action]'}. Next Step: ${form.msme_visible_next_step || form.recommendations || '[Next Step]'}. Together we grow ${selectedMsme.business_name}! — PRUDEV II / GOPA Pro`}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{
        borderTop: '1px solid #E8EDF2',
        px: { xs: 1.5, sm: 3 }, py: 1.5, gap: 1, flexWrap: 'wrap',
      }}>
        <Button onClick={onClose} disabled={saving} sx={{ order: { xs: 2, sm: 0 } }}>
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" startIcon={<Save />} disabled={saving}
          sx={{ flex: { xs: '1 1 140px', sm: '0 0 auto' } }}
          onClick={() => save(false)}>
          {saving ? <CircularProgress size={16} /> : 'Save Draft'}
        </Button>
        <Button variant="contained" startIcon={<Send />} disabled={saving}
          onClick={() => save(true)}
          sx={{ bgcolor: typeInfo.color, flex: { xs: '1 1 150px', sm: '0 0 auto' } }}>
          {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Submit Report'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
